import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { STATES } from '@shared/constants';
import { getSessions, getBatchSessions } from '@/lib/api/sessions';
import { useAuth } from '@/hooks/use-auth';
import { sortSessionsByCode } from '@/utils/session-utils';
import { ALL_STATES_ID, SESSION_GROUP_IDS, isSessionGroupId } from '@shared/legislative';

interface LegislativeContextType {
  currentState: string;
  currentSession: string;
  setCurrentState: (stateId: string) => void;
  setCurrentSession: (sessionId: string) => void;
  availableSessions: Array<{ id: string; name: string; isDefault?: boolean }>;
  availableStates: Array<{ id: string; name: string }>;
  getDefaultSession: (stateId: string) => string;
  getSessionsForState: (
    stateId: string,
  ) => Promise<Array<{ id: string; name: string; isDefault?: boolean }>>;
  getSessionsForMultipleStates: (
    stateCodes: string[],
  ) => Promise<Record<string, Array<{ id: string; name: string; isDefault?: boolean }>>>;
  formatBillId: (
    stateId: string,
    sessionId: string,
    billType: string,
    billNumber: number | string,
  ) => string;
  parseBillId: (
    fullBillId: string,
  ) => { stateId: string; sessionId: string; billType: string; billNumber: string } | null;
}

const LegislativeContext = createContext<LegislativeContextType | undefined>(undefined);

interface LegislativeProviderProps {
  children: ReactNode;
}

export function LegislativeProvider({ children }: LegislativeProviderProps) {
  const { allowedStates, isAuthenticated } = useAuth();
  const filteredStates =
    allowedStates.length > 0 ? STATES.filter((s) => allowedStates.includes(s.id)) : STATES;

  // Get initial state from localStorage or default to TX
  const getInitialState = () => {
    const savedState = localStorage.getItem('selectedState');
    if (savedState && filteredStates.find((s) => s.id === savedState)) {
      return savedState;
    }
    return filteredStates.find((s) => s.id === 'US')?.id ?? filteredStates[0].id;
  };

  // Get initial session from localStorage
  const getInitialSession = () => {
    return localStorage.getItem('selectedSession') || '';
  };

  const [currentState, setCurrentState] = useState<string>(getInitialState);
  const [sessionsCache, setSessionsCache] = useState<
    Record<string, Array<{ id: string; name: string; isDefault?: boolean }>>
  >({});
  const [currentSession, setCurrentSession] = useState<string>(getInitialSession);

  // Save state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('selectedState', currentState);
  }, [currentState]);

  // Save session to localStorage whenever it changes
  useEffect(() => {
    if (currentSession) {
      localStorage.setItem('selectedSession', currentSession);
    }
  }, [currentSession]);

  // Fetch sessions for the initial state
  useEffect(() => {
    // Only fetch sessions if user is authenticated
    if (isAuthenticated) {
      const stateToLoad = getInitialState();
      getSessions({ stateCode: stateToLoad }).then((data) =>
        setSessionsCache((prev) => ({ ...prev, [stateToLoad]: sortSessionsByCode(data) })),
      );
    }
  }, [isAuthenticated]);

  // Fetch sessions when state changes and update current session
  useEffect(() => {
    // Only fetch sessions if user is authenticated
    if (isAuthenticated) {
      const stateSessions = sessionsCache[currentState];
      if (!stateSessions) {
        void getSessions({ stateCode: currentState }).then((data) => {
          const nextSessions = currentState === ALL_STATES_ID ? data : sortSessionsByCode(data);
          setSessionsCache((prev) => ({ ...prev, [currentState]: nextSessions }));
          if (!currentSession && nextSessions.length > 0) {
            setCurrentSession(nextSessions[0].id);
          }
        });
      } else {
        let hasCurrent = false;
        for (const session of stateSessions) {
          if (session.id === currentSession) {
            hasCurrent = true;
            break;
          }
        }

        if (!hasCurrent) {
          const fallback = getDefaultSession(currentState);
          setCurrentSession(fallback);
        }
      }
    }
  }, [currentState, sessionsCache, isAuthenticated, currentSession]);

  useEffect(() => {
    if (!filteredStates.find((s) => s.id === currentState)) {
      const newState = filteredStates[0].id;
      setCurrentState(newState);
    }
  }, [allowedStates]);

  // Get all available sessions for current state
  const availableSessions = sessionsCache[currentState] || [];

  // Get default session for a state
  const getDefaultSession = (stateId: string): string => {
    const stateSessions = sessionsCache[stateId] || [];
    for (const session of stateSessions) {
      if (session.isDefault) {
        return session.id;
      }
    }
    if (stateSessions.length > 0) {
      return stateSessions[0].id;
    }
    if (stateId === ALL_STATES_ID) {
      return SESSION_GROUP_IDS.allActive;
    }
    return '';
  };

  // Get sessions for any state (fetches from database if not cached)
  const getSessionsForState = async (
    stateId: string,
  ): Promise<Array<{ id: string; name: string; isDefault?: boolean }>> => {
    // Return from cache if available
    if (sessionsCache[stateId]) {
      return sessionsCache[stateId];
    }

    // Fetch from database
    const data = await getSessions({ stateCode: stateId });
    const sessions = stateId === ALL_STATES_ID ? data : sortSessionsByCode(data);

    // Cache the result
    setSessionsCache((prev) => ({ ...prev, [stateId]: sessions }));

    return sessions;
  };

  // Get sessions for multiple states in a single request
  const getSessionsForMultipleStates = async (
    stateCodes: string[],
  ): Promise<Record<string, Array<{ id: string; name: string; isDefault?: boolean }>>> => {
    // Check which states are already cached
    const uncachedStates: string[] = [];
    const result: Record<string, Array<{ id: string; name: string; isDefault?: boolean }>> = {};

    for (const stateCode of stateCodes) {
      if (sessionsCache[stateCode]) {
        result[stateCode] = sessionsCache[stateCode];
      } else {
        uncachedStates.push(stateCode);
      }
    }

    // If all states are cached, return immediately
    if (uncachedStates.length === 0) {
      return result;
    }

    // Fetch uncached states in a single batch request
    const batchData = await getBatchSessions({
      getBatchSessionsRequest: { stateCodes: uncachedStates },
    });

    // Process and cache the batch results
    for (const [stateCode, sessions] of Object.entries(batchData)) {
      const sortedSessions = stateCode === ALL_STATES_ID ? sessions : sortSessionsByCode(sessions);
      result[stateCode] = sortedSessions;
      setSessionsCache((prev) => ({ ...prev, [stateCode]: sortedSessions }));
    }

    return result;
  };

  const handleSetCurrentState = (stateId: string) => {
    setCurrentState(stateId);
    if (stateId === ALL_STATES_ID && !isSessionGroupId(currentSession)) {
      setCurrentSession('');
    }
  };

  const handleSetCurrentSession = (sessionId: string) => {
    setCurrentSession(sessionId);
  };

  // Format a bill ID from its components
  const formatBillId = (
    stateId: string,
    sessionId: string,
    billType: string,
    billNumber: number | string,
  ): string => {
    return `${stateId}-${sessionId}-${billType}-${billNumber}`;
  };

  // Parse a complete bill ID into its components
  const parseBillId = (fullBillId: string) => {
    const parts = fullBillId.split('-');
    if (parts.length !== 4) return null;
    return {
      stateId: parts[0],
      sessionId: parts[1],
      billType: parts[2],
      billNumber: parts[3],
    };
  };

  return (
    <LegislativeContext.Provider
      value={{
        currentState,
        currentSession,
        setCurrentState: handleSetCurrentState,
        setCurrentSession: handleSetCurrentSession,
        availableSessions,
        availableStates: filteredStates,
        getDefaultSession,
        getSessionsForState,
        getSessionsForMultipleStates,
        formatBillId,
        parseBillId,
      }}
    >
      {children}
    </LegislativeContext.Provider>
  );
}

export function useLegislativeContext() {
  const context = useContext(LegislativeContext);
  if (context === undefined) {
    throw new Error('useLegislativeContext must be used within a LegislativeProvider');
  }
  return context;
}
